import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An error occurred' }));
    throw new Error(error.error || error.message || 'Request failed');
  }

  return response.json();
}

// User APIs
export const userApi = {
  getUser: (userId: string) => apiRequest(`/users/${userId}`),
  listUsers: (params?: { department?: string; activeOnly?: boolean }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest(`/users${query ? `?${query}` : ''}`);
  },
  createUser: (data: {
    email: string;
    firstName: string;
    lastName: string;
    jobTitle?: string;
    department?: string;
    departmentName?: string;
    site?: string;
    role?: string;
    supervisorId?: string;
    supervisorName?: string;
    reportsDirectlyToDirector?: boolean;
  }) => apiRequest<{ message: string; user: { userId: string; email: string } }>('/users', { method: 'POST', body: data }),
  updateUser: (userId: string, data: unknown) =>
    apiRequest(`/users/${userId}`, { method: 'PUT', body: data }),
  getTeamMembers: (supervisorId: string) => apiRequest(`/users/${supervisorId}/team`),
  inviteUser: (userId: string) =>
    apiRequest<{ message: string; email: string; cognitoUserId?: string }>(`/users/${userId}/invite`, { method: 'POST' }),
};

// Leave Application APIs
export const leaveApi = {
  listApplications: (params?: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/leave/applications${query ? `?${query}` : ''}`);
  },
  getApplication: (id: string) => apiRequest(`/leave/applications/${id}`),
  submitApplication: (data: unknown) =>
    apiRequest('/leave/applications', { method: 'POST', body: data }),
  recommendApplication: (id: string, data: { recommendation: string; comments?: string }) =>
    apiRequest(`/leave/applications/${id}/recommend`, { method: 'POST', body: data }),
  approveApplication: (id: string, data?: { comments?: string; approvedDays?: number; adjustmentReason?: string }) =>
    apiRequest(`/leave/applications/${id}/approve`, { method: 'POST', body: data }),
  declineApplication: (id: string, data: { reason: string }) =>
    apiRequest(`/leave/applications/${id}/decline`, { method: 'POST', body: data }),
  cancelApplication: (id: string, data: { reason: string }) =>
    apiRequest(`/leave/applications/${id}/cancel`, { method: 'POST', body: data }),
  approveCancellation: (id: string, data: { approve: boolean; comments?: string }) =>
    apiRequest(`/leave/applications/${id}/approve-cancellation`, { method: 'POST', body: data }),
};

// Balance APIs
export const balanceApi = {
  listAllBalances: (year?: number) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest(`/balances${query}`);
  },
  getBalance: (userId: string, params?: { leaveType?: string; year?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest(`/balances/${userId}${query ? `?${query}` : ''}`);
  },
  initializeBalances: (data: { userId: string; year?: number; leaveTypes?: string[] }) =>
    apiRequest('/balances/initialize', { method: 'POST', body: data }),
  adjustBalance: (data: { userId: string; leaveType: string; year: number; adjustment: number; reason: string }) =>
    apiRequest('/balances/adjust', { method: 'POST', body: data }),
  setBalance: (data: {
    userId: string;
    leaveType: string;
    year: number;
    total: number;
    taken?: number;
    pending?: number;
    carryOver?: number;
    reason: string;
  }) => apiRequest('/balances/set', { method: 'POST', body: data }),
  processCarryOver: (data: { fromYear: number; toYear: number; userId?: string; maxCarryOverDays?: number }) =>
    apiRequest('/balances/carry-over', { method: 'POST', body: data }),
  getHistory: (userId: string) => apiRequest(`/balances/${userId}/history`),
};

// Calendar APIs
export const calendarApi = {
  getEvents: (params?: { startDate?: string; endDate?: string; department?: string; status?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest(`/calendar/events${query ? `?${query}` : ''}`);
  },
  getHolidays: (year?: string) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest(`/calendar/holidays${query}`);
  },
};

// Document APIs
export const documentApi = {
  getUploadUrl: (data: { fileName: string; contentType: string; documentType: string; applicationId?: string }) =>
    apiRequest('/documents/upload-url', { method: 'POST', body: data }),
  getDownloadUrl: (key: string) => apiRequest(`/documents/download-url?key=${encodeURIComponent(key)}`),
};

// Admin APIs
export const adminApi = {
  // Holiday management
  listHolidays: (year?: string) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest(`/admin/holidays${query}`);
  },
  createHoliday: (data: { date: string; name: string; type?: 'PUBLIC' | 'SPECIAL' }) =>
    apiRequest('/admin/holidays', { method: 'POST', body: data }),
  deleteHoliday: (date: string) =>
    apiRequest(`/admin/holidays/${date}`, { method: 'DELETE' }),
  generateHolidays: (data: { year: number; overwrite?: boolean }) =>
    apiRequest('/admin/holidays/generate', { method: 'POST', body: data }),
  // Manual leave entry
  createManualLeave: (data: {
    userId: string;
    leaveType: string;
    startDate: string;
    endDate: string;
    totalDays: number;
    reason: string;
    isHalfDay?: boolean;
    halfDayPortion?: 'AM' | 'PM';
    notes?: string;
  }) => apiRequest('/admin/leave', { method: 'POST', body: data }),
};

// Payslip APIs
export interface PayslipInfo {
  payslipId: string;
  userId: string;
  employeeName: string;
  year: number;
  month: number;
  fileName: string;
  uploadedAt: string;
  uploadedByName: string;
}

export interface PayslipStatusEmployee {
  userId: string;
  employeeName: string;
  email: string;
  department: string;
  hasPayslip: boolean;
  payslipId?: string;
  uploadedAt?: string;
}

export interface PayslipStatusResponse {
  year: number;
  month: number;
  summary: {
    totalEmployees: number;
    uploadedCount: number;
    pendingCount: number;
    completionPercentage: number;
  };
  employees: PayslipStatusEmployee[];
}

export const payslipApi = {
  // Employee APIs
  listPayslips: (params?: { userId?: string; year?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest<{ payslips: PayslipInfo[]; count: number }>(`/payslips${query ? `?${query}` : ''}`);
  },
  getDownloadUrl: (userId: string, year: number, month: number) =>
    apiRequest<{ downloadUrl: string; fileName: string }>(`/payslips/${userId}/${year}/${month}/download`),

  // Admin APIs
  uploadPayslip: (data: { userId: string; year: number; month: number; fileName: string; contentType: string }) =>
    apiRequest<{ uploadUrl: string; payslipId: string; s3Key: string }>('/admin/payslips/upload', { method: 'POST', body: data }),
  confirmUpload: (data: { userId: string; year: number; month: number }) =>
    apiRequest<{ message: string; payslipId: string }>('/admin/payslips/confirm', { method: 'POST', body: data }),
  getStatus: (year: number, month: number) =>
    apiRequest<PayslipStatusResponse>(`/admin/payslips/status?year=${year}&month=${month}`),
};
